import { FieldType, DraftProcurationFeeRule, ProcurationFeeItemType, FeeUnit } from '@pmp/api';
import { CrudMode, transformUppercaseNoSpaces } from '@ohpen/enso-ui';
import type { FormItem, RenderFormItem, SelectOption, SelectsOptions } from '@ohpen/enso-ui';
import { FormInstance, ProFormDigit, ProFormList, ProFormSelect, ProFormText } from '@ant-design/pro-components';
import { t } from 'i18next';
import { Alert, Button, Form, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { ReactElement } from 'react';

import styles from './styles.module.css';

export const getFeesFormSchema = (
  form: FormInstance | undefined,
  selectsOptions: SelectsOptions,
  mode: CrudMode | null,
  showAlert: boolean,
): ReactElement => {
  const disabled = mode === CrudMode.VIEW;

  return (
    <>
      {showAlert && <Alert message={t('lender.procurationFees.labels.procurationFeeInUseMessage')} type="warning" showIcon />}
      <ProFormText
        name="id"
        label={t('lender.procurationFees.labels.id')}
        fieldProps={{ disabled: true }}
        formItemProps={{ getValueFromEvent: transformUppercaseNoSpaces }}
        hidden={mode === CrudMode.CREATE}
      />
      <ProFormText name="name" label={t('lender.procurationFees.labels.name')} fieldProps={{ disabled: mode === CrudMode.VIEW }} required />
      <ProFormSelect
        name="feeIsAbsolute"
        label={t('lender.procurationFees.labels.feeIsAbsolute')}
        options={selectsOptions[FieldType.YES_NO]}
        fieldProps={{ disabled: mode === CrudMode.VIEW }}
        required
      />
      <ProFormList
        name="feeItems"
        min={1}
        initialValue={[
          {
            index: 0,
            procurationFeeItemType: 'COMPLETION',
            value: undefined,
            unit: undefined,
            basis: undefined,
          },
        ]}
        alwaysShowItemLabel={true}
        creatorButtonProps={{
          type: 'default',
          size: 'small',
          creatorButtonText: t('lender.procurationFees.buttons.addFee'),
          // disabled,
          disabled: true, // TODO: remove when BFF is ready to handle Trial fees
        }}
        copyIconProps={false}
        deleteIconProps={false}
      >
        {(meta, index, action) => {
          const feeItems = Form.useWatch('feeItems', form) || [];
          const currentType = feeItems?.[index]?.procurationFeeItemType;
          const currentUnit = feeItems?.[index]?.unit;
          const currentBassis = feeItems?.[index]?.basis;
          const isCompletion = currentType === ProcurationFeeItemType.COMPLETION;
          const isFlat = currentUnit === FeeUnit.FLAT;

          const completionFeeIndex = Array.isArray(feeItems)
            ? feeItems.findIndex(fee => fee.procurationFeeItemType === ProcurationFeeItemType.COMPLETION)
            : -1;
          const typeOptions = selectsOptions[FieldType.PROCURATION_FEE_ITEM_TYPE].map((option: SelectOption) => ({
            ...option,
            disabled: completionFeeIndex > -1 && completionFeeIndex !== index && option.value === ProcurationFeeItemType.COMPLETION,
          }));

          const basisOption = !isCompletion
            ? selectsOptions[FieldType.PROCURATION_FEE_BASIS]
            : selectsOptions[FieldType.PROCURATION_FEE_BASIS].filter(x => x.value !== 'OUTSTANDING_BALANCE');

          const unitOptions = selectsOptions[FieldType.PROCURATION_FEE_UNIT].map((option: SelectOption) => ({
            ...option,
            disabled: currentBassis && option.value === FeeUnit.FLAT,
          }));

          return (
            <>
              <ProFormSelect
                name="procurationFeeItemType"
                label={t('lender.procurationFees.labels.type')}
                options={typeOptions}
                allowClear={false}
                colProps={{ span: 24 }}
                disabled={disabled || isCompletion}
                required
              />
              <ProFormSelect
                name="unit"
                label={t('lender.procurationFees.labels.unit')}
                options={unitOptions}
                colProps={{ span: 8 }}
                disabled={disabled}
                required
              />
              <ProFormDigit
                name="value"
                label={t('lender.procurationFees.labels.value')}
                min={-999999999}
                colProps={{ span: 8 }}
                disabled={disabled}
                required
              />
              <ProFormSelect
                name="basis"
                label={t('lender.procurationFees.labels.basis')}
                options={basisOption}
                colProps={{ span: 8 }}
                disabled={disabled || isFlat}
              />
              <ProFormDigit
                name="startingMonth"
                label={t('lender.procurationFees.labels.startingMonth')}
                min={1}
                step={1}
                disabled={disabled}
                hidden={isCompletion}
                required
                colProps={{ span: 8 }}
              />
              <ProFormDigit
                name="intervalInMonths"
                label={t('lender.procurationFees.labels.periodInMonths')}
                precision={0}
                min={0}
                step={1}
                disabled={disabled}
                hidden={isCompletion}
                required
                colProps={{ span: 8 }}
              />
              <ProFormSelect
                name="endingType"
                label={t('lender.procurationFees.labels.endingType')}
                options={selectsOptions[FieldType.ENDING_TYPE]}
                disabled={disabled}
                hidden={isCompletion}
                required
                colProps={{ span: 8 }}
              />
              {!isCompletion && (
                <div className={styles.delete}>
                  <Tooltip key="delete" title={t('global.buttons.delete')} placement="top">
                    <Button
                      type="text"
                      aria-label="Delete exchange rate"
                      icon={<DeleteOutlined key="delete" />}
                      onClick={() => action.remove(index)}
                      disabled={isCompletion}
                    />
                  </Tooltip>
                </div>
              )}
            </>
          );
        }}
      </ProFormList>
    </>
  );
};

export const getFeeRulesFormSchema = (
  selectsOptions: SelectsOptions,
  renderAlert: RenderFormItem<DraftProcurationFeeRule>,
  mode?: CrudMode | null,
): FormItem[] => {
  const disabled = mode === CrudMode.VIEW;

  return [
    {
      renderFormItem: renderAlert,
    },
    {
      title: 'lender.procurationFees.labels.id',
      dataIndex: 'id',
      fieldProps: {
        disabled: true,
        hidden: mode === CrudMode.CREATE,
      },
    },
    {
      title: 'lender.procurationFees.labels.name',
      dataIndex: 'name',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        disabled,
      },
    },
    {
      title: 'lender.procurationFees.labels.operationType',
      dataIndex: 'operationType',
      valueType: 'select',
      tooltip: t('lender.procurationFees.labels.operationTypeTooltip'),
      fieldProps: {
        options: selectsOptions[FieldType.OPERATION_TYPE],
        disabled,
      },
    },
    {
      title: 'lender.procurationFees.labels.applicationType',
      dataIndex: 'applicationType',
      valueType: 'select',
      tooltip: t('lender.procurationFees.labels.applicationTypeTooltip'),
      fieldProps: {
        options: selectsOptions[FieldType.APPLICATION_TYPE],
        disabled,
      },
    },
    {
      title: 'lender.procurationFees.labels.propertyPurpose',
      dataIndex: 'propertyPurpose',
      valueType: 'select',
      tooltip: t('lender.procurationFees.labels.propertyPurposeTooltip'),
      fieldProps: {
        options: selectsOptions[FieldType.PROPERTY_PURPOSE],
        disabled,
        mode: 'multiple',
      },
    },
  ];
};
